import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'redux/store';
import {
  selectMeteorologyFieldsModelPerformancePentad,
  selectMeteorologyFieldsModelPerformancePeriodType,
  selectMeteorologyFieldsModelPerformanceReplotPending,
  selectMeteorologyFieldsModelPerformanceRounds,
  selectMeteorologyFieldsModelPerformanceRoundsRange,
  setFilter,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import useMapData from './useMapData';
import useOptions from './useOptions';

function mesmasRodadas(a: string[] | undefined, b: string[] | undefined) {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((rodada, i) => rodada === b[i]);
}

/**
 * Mantém as escolhas na troca entre Pêntada 1/2/3. Cada pêntada tem a sua lista de
 * rodadas: quando a lista da pêntada atual chega, as rodadas passam a ser as dela que
 * caem no intervalo escolhido no calendário. Se a troca veio com consulta já feita
 * (`replotPending`), consulta o mapa da nova pêntada — ou avisa no mapa quando nenhuma
 * rodada dela cai no intervalo. Montado na view para valer também no mobile, com o
 * painel fechado.
 */
export default function usePentadRoundsSync() {
  const periodType = useSelector(selectMeteorologyFieldsModelPerformancePeriodType);
  const pentad = useSelector(selectMeteorologyFieldsModelPerformancePentad);
  const rounds = useSelector(selectMeteorologyFieldsModelPerformanceRounds);
  const roundsRange = useSelector(selectMeteorologyFieldsModelPerformanceRoundsRange);
  const replotPending = useSelector(selectMeteorologyFieldsModelPerformanceReplotPending);
  const { pentads, currentRounds, roundsError } = useOptions();
  const { triggerMapDataRequest } = useMapData();

  useEffect(() => {
    if (periodType !== 'preset' || !pentad || pentad === 'livre') return;

    if (roundsError) {
      if (replotPending) {
        store.dispatch(setFilter({
          replotPending: false,
          mapLoading: false,
          mapError: {
            title: 'Rodadas indisponíveis',
            message: 'Não foi possível carregar as rodadas desta pêntada.',
          },
        }));
      }
      return;
    }

    // Lista da pêntada atual ainda chegando: decidir agora usaria a da pêntada anterior.
    if (!currentRounds) return;

    const base = roundsRange
      ? currentRounds.filter((rodada) => rodada >= roundsRange.start && rodada <= roundsRange.end)
      : (rounds ?? []).filter((rodada) => currentRounds.includes(rodada));
    const proximas = base.length > 0 ? base : undefined;

    if (!mesmasRodadas(proximas, rounds)) {
      store.dispatch(setFilter({ rounds: proximas }));
      return;
    }

    if (!replotPending) return;

    if (proximas) {
      store.dispatch(setFilter({ replotPending: false }));
      triggerMapDataRequest({ preferCache: true });
      return;
    }

    const rotulo = pentads.find((p) => p.id === pentad)?.label ?? 'pêntada';
    store.dispatch(setFilter({
      replotPending: false,
      mapLoading: false,
      mapError: {
        title: 'Sem rodadas nesta pêntada',
        message: `Nenhuma rodada da ${rotulo} cai no intervalo escolhido. Ajuste o intervalo em Rodadas.`,
      },
    }));
  }, [periodType, pentad, rounds, roundsRange, replotPending,
    pentads, currentRounds, roundsError, triggerMapDataRequest]);
}
